import { useEffect, useRef } from 'react';
import { inAppOf, loadBucket, stayBucket, viaOf } from './arrival.js';
import { sendNow, track } from './track.js';

// Once per page load: Back from the PIN to the welcome is not a second arrival.
let seen = false;
let left = false;

/**
 * What a newcomer did on the welcome, so the funnel can tell an instant
 * bounce from someone who read, scrolled the book and then left:
 *
 * - `welcome_seen` once, with where they came from (`via`), the app whose
 *   built-in browser they are in (`app`), and how long the page took (`load`);
 * - `welcome_scroll` once, the first time they scroll;
 * - `welcome_left` once, when the page is hidden or closed while the welcome
 *   is still showing, with how long they looked (`secs`) and whether they
 *   tapped or scrolled anything.
 *
 * All of it is coarse and anonymous: a name from a fixed list, rounded
 * times and two yes-or-no answers, keyed by the device id `track` uses.
 */
export function useWelcomeWatch(where: 'web' | 'telegram'): {
  /** Call on any tap inside the welcome. */
  tapped: () => void;
} {
  const tappedRef = useRef(false);
  const scrolledRef = useRef(false);
  useEffect(() => {
    const shownAt = performance.now();
    if (!seen) {
      seen = true;
      track('welcome_seen', {
        via: where === 'telegram' ? 'telegram' : viaOf(window.location.search, document.referrer),
        app: where === 'telegram' ? 'telegram' : inAppOf(navigator.userAgent),
        load: loadBucket(shownAt),
      });
    }
    const onScroll = (): void => {
      if (scrolledRef.current || window.scrollY < 60) return;
      scrolledRef.current = true;
      track('welcome_scroll');
    };
    const leave = (): void => {
      if (left) return;
      left = true;
      track('welcome_left', {
        secs: stayBucket(performance.now() - shownAt),
        tapped: tappedRef.current,
        scrolled: scrolledRef.current,
      });
      sendNow();
    };
    const onHidden = (): void => {
      if (document.visibilityState === 'hidden') leave();
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    document.addEventListener('visibilitychange', onHidden);
    window.addEventListener('pagehide', leave);
    return () => {
      window.removeEventListener('scroll', onScroll);
      document.removeEventListener('visibilitychange', onHidden);
      window.removeEventListener('pagehide', leave);
    };
  }, [where]);
  return {
    tapped: () => {
      tappedRef.current = true;
    },
  };
}
